import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (!token) return setError("Missing reset token.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout eyebrow="All set" title="Password updated" subtitle="Redirecting you to sign in…">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[#22C55E]/8 border border-[#22C55E]/20 text-[#166534]">
          <CheckCircle2 className="w-5 h-5" /> <span className="text-[15px]">Your password has been reset securely.</span>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Reset password" title="Choose a new password" subtitle="Make it strong. Your operations depend on it.">
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="lux-label">New password</label>
          <input data-testid="reset-password-input" className="lux-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
        </div>
        <div>
          <label className="lux-label">Confirm password</label>
          <input data-testid="reset-confirm-input" className="lux-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
        </div>
        {error && <div data-testid="reset-error" className="text-[15px] text-[#EF4444] bg-[#EF4444]/8 border border-[#EF4444]/20 rounded-xl px-4 py-3">{error}</div>}
        <button data-testid="reset-submit-btn" className="lux-btn-primary w-full flex items-center justify-center" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
        </button>
        <p className="text-center text-[15px] text-nexa-mute pt-2">
          <Link to="/login" className="lux-link">Back to sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
